import React from 'react';
import { Card, CardContent } from '../ui/card';
import { formatDate, formatCurrency } from '../../lib/utils';

/**
 * Printable Invoice Template
 * Uses institution settings and bill data
 */
export default function InvoiceTemplate({ invoice, settings }) {
  if (!invoice) return null;

  const institutionDetails = invoice.institution_details || settings || {};
  const patientDetails = invoice.patient_details || {};

  return (
    <div className="invoice-template bg-white p-8 max-w-4xl mx-auto" style={{ fontFamily: 'Arial, sans-serif' }}>
      {/* Header */}
      <div className="border-b-2 border-slate-800 pb-4 mb-6">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">{institutionDetails.name || 'Hospital Name'}</h1>
            <p className="text-sm text-slate-600 mt-1">{institutionDetails.address}</p>
            <p className="text-sm text-slate-600">
              {institutionDetails.city}, {institutionDetails.state} - {institutionDetails.pincode}
            </p>
            <p className="text-sm text-slate-600 mt-1">
              Phone: {institutionDetails.phone} | Email: {institutionDetails.email}
            </p>
            {institutionDetails.website && (
              <p className="text-sm text-slate-600">Website: {institutionDetails.website}</p>
            )}
          </div>
          <div className="text-right">
            <h2 className="text-2xl font-bold text-red-600">INVOICE</h2>
            <p className="text-sm text-slate-600 mt-1">#{invoice.invoice_number}</p>
            <p className="text-sm text-slate-600">Date: {formatDate(invoice.generated_at)}</p>
          </div>
        </div>
        
        {/* Registration Numbers */}
        <div className="mt-3 text-sm text-slate-600 flex gap-4 flex-wrap">
          {institutionDetails.gstin && institutionDetails.show_gstin !== false && (
            <span><strong>GSTIN:</strong> {institutionDetails.gstin}</span>
          )}
          {institutionDetails.pan && institutionDetails.show_pan && (
            <span><strong>PAN:</strong> {institutionDetails.pan}</span>
          )}
          {institutionDetails.license_number && institutionDetails.show_license !== false && (
            <span><strong>License:</strong> {institutionDetails.license_number}</span>
          )}
          {institutionDetails.registration_number && (
            <span><strong>Reg. No:</strong> {institutionDetails.registration_number}</span>
          )}
        </div>
        
        {/* Custom Header */}
        {institutionDetails.header_text && (
          <div className="mt-3 p-2 bg-blue-50 rounded text-sm text-blue-800 italic">
            {institutionDetails.header_text}
          </div>
        )}
      </div>

      {/* Bill To / Patient Details */}
      <div className="mb-6">
        <h3 className="text-sm font-bold text-slate-900 mb-2">BILL TO:</h3>
        <div className="bg-slate-50 p-4 rounded">
          <p className="font-semibold text-slate-900">{patientDetails.name}</p>
          {patientDetails.patient_id && (
            <p className="text-sm text-slate-600">Patient ID: {patientDetails.patient_id}</p>
          )}
          <p className="text-sm text-slate-600 mt-1">{patientDetails.address}</p>
          <div className="flex gap-4 mt-1 text-sm text-slate-600">
            {patientDetails.phone && <span>Phone: {patientDetails.phone}</span>}
            {patientDetails.email && <span>Email: {patientDetails.email}</span>}
          </div>
          {patientDetails.gstin && (
            <p className="text-sm text-slate-600 mt-1"><strong>GSTIN:</strong> {patientDetails.gstin}</p>
          )}
        </div>
      </div>

      {/* Items Table */}
      <table className="w-full mb-6">
        <thead>
          <tr className="bg-slate-800 text-white">
            <th className="text-left p-2 text-sm">#</th>
            <th className="text-left p-2 text-sm">Description</th>
            <th className="text-center p-2 text-sm">Category</th>
            <th className="text-center p-2 text-sm">Qty</th>
            <th className="text-right p-2 text-sm">Rate</th>
            <th className="text-right p-2 text-sm">Amount</th>
          </tr>
        </thead>
        <tbody>
          {invoice.items && invoice.items.map((item, index) => (
            <tr key={`${item.description}-${item.quantity}-${item.unit_price}`} className="border-b border-slate-200">
              <td className="p-2 text-sm">{index + 1}</td>
              <td className="p-2 text-sm">{item.description}</td>
              <td className="p-2 text-sm text-center">{item.category}</td>
              <td className="p-2 text-sm text-center">{item.quantity}</td>
              <td className="p-2 text-sm text-right">{formatCurrency(item.unit_price)}</td>
              <td className="p-2 text-sm text-right font-semibold">{formatCurrency(item.total)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Totals */}
      <div className="flex justify-end mb-6">
        <div className="w-64">
          <div className="flex justify-between py-1 text-sm">
            <span className="text-slate-600">Subtotal:</span>
            <span className="font-semibold">{formatCurrency(invoice.subtotal)}</span>
          </div>
          {invoice.discount_amount > 0 && (
            <div className="flex justify-between py-1 text-sm text-green-600">
              <span>Discount:</span>
              <span className="font-semibold">- {formatCurrency(invoice.discount_amount)}</span>
            </div>
          )}
          {invoice.tax_amount > 0 && (
            <div className="flex justify-between py-1 text-sm">
              <span className="text-slate-600">Tax:</span>
              <span className="font-semibold">+ {formatCurrency(invoice.tax_amount)}</span>
            </div>
          )}
          <div className="flex justify-between py-2 text-lg border-t-2 border-slate-800 mt-2">
            <span className="font-bold">Total:</span>
            <span className="font-bold text-red-600">{formatCurrency(invoice.total_amount)}</span>
          </div>
          {invoice.paid_amount > 0 && (
            <>
              <div className="flex justify-between py-1 text-sm text-green-600">
                <span>Paid:</span>
                <span className="font-semibold">- {formatCurrency(invoice.paid_amount)}</span>
              </div>
              <div className="flex justify-between py-2 text-base border-t border-slate-300">
                <span className="font-bold">Balance Due:</span>
                <span className="font-bold text-amber-600">{formatCurrency(invoice.balance_amount)}</span>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Payment Status */}
      <div className="mb-6">
        <div className="inline-block px-4 py-2 rounded text-sm font-semibold" style={{
          backgroundColor: invoice.payment_status === 'paid' ? '#dcfce7' : invoice.payment_status === 'partial' ? '#fef3c7' : '#fee2e2',
          color: invoice.payment_status === 'paid' ? '#166534' : invoice.payment_status === 'partial' ? '#92400e' : '#991b1b'
        }}>
          Payment Status: {invoice.payment_status.toUpperCase()}
        </div>
      </div>

      {/* Terms and Conditions */}
      {institutionDetails.terms_and_conditions && (
        <div className="mb-4 p-3 bg-slate-50 rounded">
          <h4 className="text-sm font-bold text-slate-900 mb-1">Terms & Conditions:</h4>
          <p className="text-xs text-slate-600 whitespace-pre-line">{institutionDetails.terms_and_conditions}</p>
        </div>
      )}

      {/* Footer */}
      <div className="border-t-2 border-slate-800 pt-4 mt-6 text-center">
        {institutionDetails.footer_text && (
          <p className="text-sm text-slate-600 italic mb-2">{institutionDetails.footer_text}</p>
        )}
        <p className="text-xs text-slate-500">Generated by: {invoice.generated_by_name} on {formatDate(invoice.generated_at)}</p>
        <p className="text-xs text-slate-400 mt-2">This is a computer-generated invoice</p>
      </div>
    </div>
  );
}
